import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Clock,
  Play,
  Pause,
  RefreshCw,
  Trash2,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Send,
  MessageSquare,
  Mail,
  Calendar,
  User,
  Stethoscope,
  RotateCcw
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: Clock },
  processing: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: Loader2 },
  sent: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: CheckCircle2 },
  failed: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: XCircle },
  cancelled: { color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300', icon: Pause }
};

const TYPE_CONFIG = {
  reminder_24h: { icon: Calendar, label: 'Recordatorio 24h' },
  reminder_1h: { icon: Clock, label: 'Recordatorio 1h' },
  reminder_custom: { icon: Clock, label: 'Recordatorio' },
  review_request: { icon: MessageSquare, label: 'Solicitud de Reseña' },
  confirmation_request: { icon: CheckCircle2, label: 'Confirmación' },
  follow_up: { icon: RefreshCw, label: 'Seguimiento' },
  medication_reminder: { icon: AlertCircle, label: 'Recordatorio Medicación' },
  appointment_notification: { icon: Calendar, label: 'Notificación Cita' }
};

const TARGET_CONFIG = {
  patient: { icon: User, label: 'Paciente' },
  specialist: { icon: Stethoscope, label: 'Especialista' },
  admin: { icon: User, label: 'Administrador' },
  insurance: { icon: User, label: 'Aseguradora' }
};

const CHANNEL_CONFIG = {
  whatsapp: { icon: MessageSquare, label: 'WhatsApp', color: 'text-green-600' },
  email: { icon: Mail, label: 'Email', color: 'text-blue-600' },
  sms: { icon: Send, label: 'SMS', color: 'text-purple-600' }
};

export const ScheduledTasksPage = () => {
  const { t } = useTranslation(['scheduledTasks', 'common']);
  const [activeTab, setActiveTab] = useState('pending');
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [cancelDialog, setCancelDialog] = useState({ open: false, taskId: null });

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [activeTab]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = activeTab === 'all' ? {} : { status: activeTab };
      const response = await axios.get(`${API_URL}/api/scheduled-tasks`, { params });
      setTasks(response.data.data || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scheduled-tasks/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleProcessAll = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/api/scheduled-tasks/process`);
      toast.success(t('messages.tasksProcessed', { count: response.data.processed }));
      fetchTasks();
      fetchStats();
    } catch (error) {
      toast.error(t('messages.errorProcessing'));
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessSingle = async (taskId) => {
    try {
      await axios.post(`${API_URL}/api/scheduled-tasks/process/${taskId}`);
      toast.success(t('messages.taskProcessed'));
      fetchTasks();
      fetchStats();
    } catch (error) {
      toast.error(t('messages.errorProcessing'));
    }
  };

  const handleCancel = async () => {
    try {
      await axios.post(`${API_URL}/api/scheduled-tasks/${cancelDialog.taskId}/cancel`);
      toast.success(t('messages.taskCancelled'));
      fetchTasks();
      fetchStats();
      setCancelDialog({ open: false, taskId: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorProcessing'));
    }
  };

  const handleRetry = async (taskId) => {
    try {
      await axios.post(`${API_URL}/api/scheduled-tasks/${taskId}/retry`);
      toast.success('Tarea reprogramada');
      fetchTasks();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorProcessing'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('es-CO', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="scheduled-tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
        <Button onClick={handleProcessAll} disabled={processing}>
          {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
          {t('actions.processAll')}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('stats.pendingDue')}</p>
                  <p className="text-2xl font-bold">{stats.pending_due || 0}</p>
                </div>
                <div className="p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                  <AlertCircle className="h-6 w-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('stats.upcoming24h')}</p>
                  <p className="text-2xl font-bold">{stats.upcoming_24h || 0}</p>
                </div>
                <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('stats.sent')}</p>
                  <p className="text-2xl font-bold">{stats.by_status?.sent || 0}</p>
                </div>
                <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('stats.failedLast24h')}</p>
                  <p className="text-2xl font-bold">{stats.failed_last_24h || 0}</p>
                </div>
                <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tasks List */}
      <Card>
        <CardHeader>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="pending">{t('tasks.pending')}</TabsTrigger>
              <TabsTrigger value="sent">{t('tasks.sent')}</TabsTrigger>
              <TabsTrigger value="failed">{t('tasks.failed')}</TabsTrigger>
              <TabsTrigger value="cancelled">{t('tasks.cancelled')}</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('tasks.empty')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map(task => {
                const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
                const StatusIcon = statusConfig.icon;
                const typeConfig = TYPE_CONFIG[task.task_type] || { icon: Clock, label: task.task_type };
                const TypeIcon = typeConfig.icon;
                const targetConfig = TARGET_CONFIG[task.target_type] || { icon: User, label: task.target_type };
                const TargetIcon = targetConfig.icon;
                const channelConfig = CHANNEL_CONFIG[task.channel] || { icon: Send, label: task.channel };
                const ChannelIcon = channelConfig.icon;

                return (
                  <div
                    key={task.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${statusConfig.color}`}>
                        <StatusIcon className={`h-5 w-5 ${task.status === 'processing' ? 'animate-spin' : ''}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <TypeIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{typeConfig.label}</span>
                          <Badge variant="outline" className="text-xs">
                            <TargetIcon className="h-3 w-3 mr-1" />
                            {targetConfig.label}
                          </Badge>
                          <Badge variant="outline" className={`text-xs ${channelConfig.color}`}>
                            <ChannelIcon className="h-3 w-3 mr-1" />
                            {channelConfig.label}
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          <span>{task.target_contact}</span>
                          {task.order_id && (
                            <span className="ml-2">• Orden: {task.order_id.slice(0, 8)}...</span>
                          )}
                        </div>
                        {task.error_message && (
                          <p className="text-xs text-destructive mt-1">{task.error_message}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm font-medium">{formatDate(task.scheduled_for)}</p>
                        {task.retry_count > 0 && (
                          <p className="text-xs text-muted-foreground">
                            Reintentos: {task.retry_count}/{task.max_retries}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {task.status === 'pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleProcessSingle(task.id)}
                              title={t('actions.process')}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCancelDialog({ open: true, taskId: task.id })}
                              title={t('actions.cancel')}
                            >
                              <Pause className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {(task.status === 'failed' || task.status === 'cancelled') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRetry(task.id)}
                            title={t('actions.retry')}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel Dialog */}
      <AlertDialog open={cancelDialog.open} onOpenChange={(open) => setCancelDialog({ ...cancelDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar Tarea</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea cancelar esta tarea programada? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancel} className="bg-destructive hover:bg-destructive/90">
              Cancelar Tarea
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ScheduledTasksPage;
